import PropTypes from 'prop-types'
import {
  createContext,
  forwardRef,
  memo,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react'
import useFallbackRef from 'lib/react/hooks/useFallbackRef'
import classNames from 'lib/util/classNames'
import noop from 'lib/util/noop'
import Indicators from './Indicators/Indicators'
import Navigation from './Navigation/Navigation'
import Slide from './Slide/Slide'
import Slides from './Slides/Slides'
import styles from './Slideshow.module.scss'
import TimedIndicators from './TimedIndicators/TimedIndicators'

// Create the context
const SlideshowContext = createContext({
  hasOverflowLeft: false,
  hasOverflowRight: false,
  numSlides: 0,
  onScrollToSlide: noop,
  setGroupCount: noop,
  setHasOverflowLeft: noop,
  setHasOverflowRight: noop,
  setNumSlides: noop,
  setSlideIndex: noop,
  setSlideTargetIndex: noop,
  slideIndex: 0,
  slideTargetIndex: 0
})

// Export the context as a hook
export const useSlideshowContext = () => {
  const context = useContext(SlideshowContext)

  return context
}

/**
 * The `Slideshow`
 *
 * @param {object} props - the component props
 * @returns {React.ReactElement} the element
 */
const SlideshowComponent = forwardRef((props, forwardedRef) => {
  const { children, className } = props
  const ref = useFallbackRef(forwardedRef)
  const [numSlides, setNumSlides] = useState(0)
  const [slideIndex, setSlideIndex] = useState(0)
  const [groupCount, setGroupCount] = useState(1)
  const [slideTargetIndex, setSlideTargetIndex] = useState(0)
  const [hasOverflowRight, setHasOverflowRight] = useState(false)
  const [hasOverflowLeft, setHasOverflowLeft] = useState(false)
  const [isInteracting, setIsInteracting] = useState(false)
  const classNameOutput = useMemo(
    () => classNames([className, styles.container]),
    [className]
  )

  /**
   * Handle scroll to slide
   */
  const handleScrollToSlide = useCallback(index => {
    setSlideTargetIndex(index)
  }, [])

  /**
   * Memo: The context object
   */
  const ctx = useMemo(
    () => ({
      groupCount,
      hasOverflowLeft,
      hasOverflowRight,
      isInteracting,
      numSlides,
      onScrollToSlide: handleScrollToSlide,
      setGroupCount,
      setHasOverflowLeft,
      setHasOverflowRight,
      setIsInteracting,
      setNumSlides,
      setSlideIndex,
      slideIndex,
      slideTargetIndex
    }),
    [
      groupCount,
      handleScrollToSlide,
      hasOverflowLeft,
      hasOverflowRight,
      isInteracting,
      numSlides,
      setGroupCount,
      setHasOverflowLeft,
      setHasOverflowRight,
      setIsInteracting,
      setNumSlides,
      setSlideIndex,
      slideIndex,
      slideTargetIndex
    ]
  )

  return (
    <SlideshowContext.Provider value={ctx}>
      <div ref={ref} className={classNameOutput}>
        {children}
      </div>
    </SlideshowContext.Provider>
  )
})

/** @type {object} */
SlideshowComponent.displayName = 'Slideshow'

/** @type {object} */
SlideshowComponent.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string
}

SlideshowComponent.defaultProps = {
  children: null,
  className: ''
}

const Slideshow = memo(SlideshowComponent)

// Bind the types
Slideshow.Slide = Slide
Slideshow.Slides = Slides
Slideshow.TimedIndicators = TimedIndicators
Slideshow.Indicators = Indicators
Slideshow.Navigation = Navigation

// Memoize
export default Slideshow
